// The list footer: how far through the records you are, how to move, and —
// folded away until asked for — how many come at a time and how they load.
//
// Lifted out of ListView because it is not the grid's: every view of a
// transaction is a view of the SAME loaded page of the SAME query — the host
// (TxnScreen) owns the offset, the size and the mode, and hands the identical
// object to whichever view is on screen. The grid, the compact card stack and
// the process view's records rail therefore page the same way, with the same
// controls in the same order, rather than each inventing its own footer. A
// second implementation here would be a second set of defaults to keep in
// step, and the compact list's lone "Load more" button was exactly that.
//
// ── why it collapses ─────────────────────────────────────────────────────
// It used to lay everything out at once: a labelled "Rows per page" select, a
// labelled Continuous ⇆ Pages toggle, and the counts. Side by side under a
// full-width grid that reads fine; in the process view's 300px rail it wrapped
// to THREE stacked rows and ate the bottom of the panel — the records, which
// are the point, lost the space to the chrome about the records.
//
// So the bar is ONE line, always, at every width: where you are and how to
// move. Rows-per-page and the load mode are settings — chosen once and then
// left alone — and they live behind the ⚙ button, which wears the current size
// so nothing has to be opened to read it back.

import { useState } from 'react'
import {
  ChevronDown, ChevronLeft, ChevronRight, Files, Infinity as InfinityIcon, Settings,
} from 'lucide-react'

import { dockedRightPadding } from '../../floatingDock'
import { tw } from '../../testHooks'

/** What a host must hand a view for it to draw this bar.
 *
 *  The infinite half alone is enough (a surface that only appends older
 *  records — an embed preview — supplies just those, and gets the single
 *  button it used to get). Supply the dual-mode half as well and the full
 *  footer appears. */
export interface ListPagination {
  loadedCount: number
  total: number
  hasMore: boolean
  loadingMore: boolean
  onLoadEarlier: () => void
  // ── Optional dual-mode controls ──────────────────────────────────────
  mode?: 'infinite' | 'paged'
  onSetMode?: (mode: 'infinite' | 'paged') => void
  pageSize?: number
  pageSizeOptions?: number[]
  onSetPageSize?: (size: number) => void
  page?: number               // 0-based index of the current page (Paged)
  onGotoPage?: (page: number) => void
}

type PaginationProps = ListPagination

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 200]

// Footer pagination controls. Always renders the legacy "Load earlier"
// affordance when no dual-mode callbacks are supplied; when they are, the one
// line carries the counts and the navigator for the active mode, and the ⚙
// button opens the page-size selector and the Continuous ⇆ Paged toggle.
export default function PaginationBar({ pagination: p, busy, hasRows }: {
  pagination: PaginationProps
  busy: boolean
  hasRows: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const dual = !!(p.onSetMode && p.onSetPageSize && p.onGotoPage)
  const mode = p.mode || 'infinite'
  const pageSize = p.pageSize || 50
  const page = p.page || 0
  const pageCount = Math.max(1, Math.ceil((p.total || 0) / Math.max(1, pageSize)))
  const disabled = p.loadingMore || busy

  // Legacy / non-dual surfaces: only the append button, and only when there
  // is more to load — unchanged behaviour for existing embed previews.
  if (!dual) {
    if (!p.hasMore) return null
    return (
      <div style={barStyle(hasRows)}>
        <button
          className="btn btn-ghost btn-sm"
          disabled={disabled}
          onClick={p.onLoadEarlier}
        >
          <ChevronDown size={14} />
          {p.loadingMore
            ? 'Loading earlier…'
            : `Load earlier entries (${p.loadedCount} of ${p.total})`}
        </button>
      </div>
    )
  }

  const options = p.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS
  // Ensure the active size is selectable even if it came from designer config
  // and isn't one of the standard options.
  const sizeOptions = options.includes(pageSize)
    ? options
    : [...options, pageSize].sort((a, b) => a - b)

  // Window label for the active page in Paged mode (e.g. "1–50").
  const first = p.total === 0 ? 0 : page * pageSize + 1
  const last = Math.min(p.total, (page + 1) * pageSize)
  const modeLabel = mode === 'infinite' ? 'Continuous' : 'Pages'

  return (
    <div style={{ flex: 'none' }}>
      {/* The settings, opened on demand. Above the bar rather than in a popup:
          the bar is already at the bottom edge of its pane, and a panel that
          simply pushes cannot be clipped by the pane that holds it. */}
      {expanded && (
        <div style={settingsPanel}>
          <label style={pagLabel}>
            <span>Rows per page</span>
            <select
              value={pageSize}
              disabled={disabled}
              onChange={(e) => p.onSetPageSize!(Number(e.target.value))}
              style={pagSelect}
            >
              {sizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <div style={modeToggle} role="group" aria-label="How records load">
            <ModeButton
              active={mode === 'infinite'}
              disabled={disabled}
              onClick={() => mode !== 'infinite' && p.onSetMode!('infinite')}
              icon={<InfinityIcon size={13} />}
              label="Continuous"
              title="Continuous scroll — keep older records loaded and append more as you go"
            />
            <ModeButton
              active={mode === 'paged'}
              disabled={disabled}
              onClick={() => mode !== 'paged' && p.onSetMode!('paged')}
              icon={<Files size={13} />}
              label="Pages"
              title="Page through fixed-size pages of records (lighter on large tables)"
            />
          </div>
        </div>
      )}

      <div style={{ ...barStyle(hasRows), justifyContent: 'space-between', gap: 12 }}>
        {/* Where you are. The one thing allowed to give up width. */}
        <span style={{ ...pagInfo, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {p.total === 0 ? 'No records'
            : mode === 'infinite'
              ? `1–${p.loadedCount.toLocaleString()} of ${p.total.toLocaleString()}`
              : `${first.toLocaleString()}–${last.toLocaleString()} of ${p.total.toLocaleString()}`}
        </span>

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flex: 'none' }}>
          {/* How to move, for the active mode. */}
          {mode === 'infinite' ? (
            p.hasMore && (
              <button
                type="button"
                disabled={disabled}
                onClick={p.onLoadEarlier}
                {...tw('action', 'load_more')}
                title="Load the next page of records below these"
                style={{ ...footBtn, width: 'auto', padding: '0 10px', gap: 4 }}
              >
                <ChevronDown size={16} />
                {p.loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )
          ) : (
            <>
              <button
                type="button"
                disabled={disabled || page <= 0}
                onClick={() => p.onGotoPage!(page - 1)}
                aria-label="Previous page"
                {...tw('action', 'prev_page')}
                title={`Previous page (${page + 1} of ${pageCount})`}
                style={footBtn}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                disabled={disabled || page >= pageCount - 1}
                onClick={() => p.onGotoPage!(page + 1)}
                aria-label="Next page"
                {...tw('action', 'next_page')}
                title={`Next page (${page + 1} of ${pageCount})`}
                style={footBtn}
              >
                <ChevronRight size={16} />
              </button>
            </>
          )}
          {/* Rows per page and how records load, opened on demand. */}
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={`Page settings — ${pageSize} per page, ${modeLabel}`}
            title={`${pageSize} per page · ${modeLabel} — click to change how records load`}
            onClick={() => setExpanded((v) => !v)}
            style={{ ...footBtn, color: expanded ? 'var(--color-accent, #0061D0)' : footBtn.color }}
          >
            <Settings size={16} />
          </button>
        </span>
      </div>
    </div>
  )
}

const footBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34,
  borderRadius: 8, border: '1px solid transparent', background: 'transparent',
  color: 'var(--color-text-secondary, #465062)', cursor: 'pointer', padding: 0,
  fontSize: '0.8125rem', fontFamily: 'inherit', whiteSpace: 'nowrap',
}

function ModeButton({
  active, disabled, onClick, icon, label, title,
}: {
  active: boolean
  disabled: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  title: string
}) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={title}
      style={modeBtn(active, hover && !disabled)}
    >
      {icon}
      {label}
    </button>
  )
}

const barStyle = (hasRows: boolean): React.CSSProperties => ({
  display: 'flex', justifyContent: 'center', alignItems: 'center',
  gap: 8, padding: '8px 16px 8px 20px',
  // One line at every width. The bar is chrome ABOUT the records; wrapping it
  // to three rows in a narrow pane spends the records' own space on it.
  flexWrap: 'nowrap',
  // Never squeezed away by the scroller above it in a column flex parent.
  flex: 'none',
  // Reserve whatever floats over the bottom-right corner (the Assistant's
  // launcher). Without it the pagination controls run underneath a button
  // they cannot be clicked through. Zero when nothing is docked there.
  paddingRight: dockedRightPadding(16),
  background: 'var(--color-bg-secondary, #ffffff)',
  borderTop: hasRows ? '1px solid var(--color-border, #dde3ea)' : 'none',
})

// The opened settings. Wraps freely — it is asked for, so it may take the room
// it needs; the bar below it is what must never wrap.
const settingsPanel: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
  padding: '8px 12px',
  background: 'var(--color-surface-2, var(--color-bg-secondary))',
  borderTop: '1px solid var(--color-border)',
}

const pagLabel: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
  fontSize: '0.9125rem', fontWeight: 500, color: 'var(--color-text-secondary)',
}

const pagSelect: React.CSSProperties = {
  padding: '4px 8px',
  background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 6,
  color: 'var(--color-text)',
  fontSize: '0.9125rem', fontWeight: 600,
  cursor: 'pointer',
}

const pagInfo: React.CSSProperties = {
  fontSize: '0.8625rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap',
}


const modeToggle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 2,
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  borderRadius: 8, padding: 2,
}

const modeBtn = (active: boolean, hover = false): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '3px 12px', fontSize: '0.8225rem', fontWeight: 600,
  border: 'none', borderRadius: 6,
  // --color-accent is the only brand colour defined in both themes
  // (--color-primary is undefined → an active fill of that renders
  // transparent, which made the active label illegible).
  background: active
    ? 'var(--color-accent)'
    : hover ? 'var(--color-bg-secondary)' : 'transparent',
  color: active ? '#fff' : 'var(--color-text)',
  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.18)' : 'none',
  cursor: active ? 'default' : 'pointer',
  transition: 'background 0.12s ease',
})
